<script setup>
import { useRestoGLobalStore } from '@/stores/restoGlobalStore'
import { useThemeStore } from '@/stores/themeStore'
import heroBannerImg from '@/assets/images/hero_banner.jpg'
import { ref, onMounted, computed } from 'vue'
import { ClockIcon, InformationCircleIcon, MapPinIcon, PhoneIcon } from '@heroicons/vue/24/outline'

const restoStore = useRestoGLobalStore()
const themeStore = useThemeStore()
const restoInfo = restoStore.restoInfo
const isLoaded = ref(false)
const isDark = computed(() => themeStore.theme === 'dark')
const showInfoModal = ref(false)
const name = `${restoStore?.restoInfo?.name}`
const address = `${restoStore?.restoInfo?.address}`
const query = `${name} ${address}`
const mapSrc = `https://maps.google.com/maps?q=` + encodeURI(query) + `&output=embed`
const openingHours = restoInfo?.resto_opening_times?.detailed

// Get current day number (1-7, Monday to Sunday)
const getCurrentDayNumber = () => {
  const today = new Date().getDay() // JS uses 0-6 (Sunday-Saturday)
  // Convert to 1-7 (Monday-Sunday) format
  return today === 0 ? 7 : today
}

// Get today's opening time
const todayOpeningHours = computed(() => {
  const dayNumber = getCurrentDayNumber()
  return openingHours.find((day) => day.day_number === dayNumber)
})

const openInfoModal = () => {
  showInfoModal.value = true
}

const closeInfoModal = () => {
  showInfoModal.value = false
}

onMounted(() => {
  setTimeout(() => {
    isLoaded.value = true
  }, 300)
})
</script>

<template>
  <div class="hero-banner relative overflow-hidden mb-4">
    <div
      class="bg-image w-full bg-cover bg-center z-0 lg:py-8 py-4 px-3"
      :style="{
        backgroundImage: `url(${restoInfo?.dynamic_site_content?.content?.rsc_slider_dynamicsite_background_image_url || restoInfo?.dynamic_site_content?.content?.rsc_slider_mobile_background_image_urlImg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }"
    >
    <div
      :class="[
        'max-w-7xl mx-auto md:px-4 px-2 md:py-6 py-4 relative z-10 bg-white dark:bg-gray-900/70 backdrop-blur-md rounded-xl shadow-lg hover:bg-white/15 dark:hover:bg-gray-900/50 transition-all duration-300 border border-white/20 dark:border-gray-700/30',
        isLoaded ? 'content-visible' : 'content-hidden'
      ]"
    >
      <div class="flex flex-col md:flex-row items-center md:items-start lg:gap-5 gap-3">
        <div
          class="logo-container hidden md:block bg-white dark:bg-gray-800 rounded-lg shadow-sm p-2 lg:w-32 lg:h-32 w-20 h-20 flex items-center justify-center transform transition-transform duration-300"
        >
          <img
            :src="restoInfo?.logo"
            alt="Restaurant Logo"
            class="max-w-full max-h-full object-contain drop-shadow-md"
          />
        </div>
        <div class="text-content text-white text-center md:text-left w-full">
          <div class="grid md:grid-cols-2 lg:gap-5 gap-3">
            <div
              class="info-group backdrop-blur-md text-gray-800 dark:text-gray-200 bg-white/10 dark:bg-gray-800/30 rounded-xl md:px-4 sm:px-3 px-0 md:py-4 py-2 md:shadow-md hover:bg-white/15 dark:hover:bg-gray-800/40 transition-all duration-300 border border-white/30 dark:border-gray-700/30"
            >
              <h1
                class="md:text-3xl text-2xl mb-4 lg:text-4xl font-bold text-shadow-sm dark:text-gray-200 text-gray-800"
              >
                {{ restoInfo?.name }}
              </h1>
              <p class="mb-2 flex items-center md:justify-start justify-center">
                <MapPinIcon class="w-5 h-5 text-indigo-500 sm:mr-2 mr-1 flex-shrink-0" />
                <span>{{ restoInfo?.address }}</span>
              </p>
              <!-- Today's Opening Hours -->
              <p class="mt-2 flex items-center md:justify-start justify-center" v-if="todayOpeningHours">
                <ClockIcon class="w-5 h-5 text-indigo-500 sm:mr-2 mr-1 flex-shrink-0" />
                <span>
                  <span>Opens at </span>
                  <span class="font-semibold">{{ todayOpeningHours.am_pm_times.join(', ') }}</span>
                </span>
              </p>
            </div>

            <!-- Map Section -->
            <div class="bg-blue-50 dark:bg-gray-700 p-4 rounded-lg hidden md:block">
              <h4
                class="text-lg font-semibold mb-3 flex items-center text-blue-700 dark:text-blue-300"
              >
                <svg
                  class="w-5 h-5 mr-2"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  ></path>
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  ></path>
                </svg>
                Location
              </h4>
              <div class="bg-gray-200 dark:bg-gray-600 rounded-md flex items-center justify-center">
                <div v-if="mapSrc" class="w-full">
                  <iframe
                    width="100%"
                    height="180"
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    :src="mapSrc"
                    class="dark:opacity-80 transition-opacity duration-300"
                  >
                  </iframe>
                </div>
                <div v-else class="text-gray-500 dark:text-gray-400">Map not available</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.bg-image {
  background-size: cover;
  transform: scale(1);
  transition: all 0.5s ease;
}

.content-hidden {
  opacity: 0;
  transform: translateY(10px);
}

.content-visible {
  opacity: 1;
  transform: translateY(0);
  transition: all 0.6s ease;
}

/* Dark mode specific animations */
:root.dark .logo-container {
  box-shadow: 0 4px 20px rgba(255, 215, 0, 0.15);
}

.info-button {
  transform: translateY(-1px);
}

.info-button:hover {
  transform: translateY(-2px);
  box-shadow:
    0 4px 6px -1px rgba(0, 0, 0, 0.1),
    0 2px 4px -1px rgba(0, 0, 0, 0.06);
}
</style>
